/**
 * Microsoft Power Automate Integration — Admin Configuration Page
 *
 * Architecture:
 *   - Outbound (Velaro → Power Automate): customer pastes their HTTP trigger URL here.
 *     Velaro POSTs conversation data to that URL when a workflow node fires.
 *   - Inbound (Power Automate → Velaro): Power Automate calls Velaro REST endpoints
 *     using a vel_live_* API key. No Velaro webhook URL is involved.
 *
 * Backend endpoints (velaro-messaging):
 *   GET  /PowerAutomate/config   → { configured, displayName, flowUrlMasked }
 *   POST /PowerAutomate/config   → { flowUrl, displayName }
 *   POST /PowerAutomate/Webhook  → triggers the flow (called by workflow engine)
 */
import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import useSubscriptions from "@/hooks/useSubscriptions";
import { Link } from "react-router-dom";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Config {
  configured: boolean;
  displayName: string;
  flowUrlMasked: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TRIGGERS = [
  {
    id: "chat-started",
    label: "Chat Started",
    icon: "💬",
    description: "Fires when a new conversation begins. Payload includes visitor name, email, page URL, and referrer.",
    payload: ["conversationId", "contactEmail", "contactName", "pageUrl", "channel", "startedAt"]
  },
  {
    id: "chat-ended",
    label: "Chat Ended",
    icon: "✅",
    description: "Fires when a conversation resolves or is closed. Payload includes duration, sentiment, CSAT score.",
    payload: ["conversationId", "agentName", "duration", "sentiment", "csatScore", "endedAt"]
  },
  {
    id: "lead-created",
    label: "Lead Created",
    icon: "📋",
    description: "Fires when a lead record is created via the Rules Engine or a workflow action.",
    payload: ["leadId", "firstName", "lastName", "email", "phone", "company", "source"]
  },
  {
    id: "case-created",
    label: "Case Created",
    icon: "🎫",
    description: "Fires when a support case is logged via the Rules Engine or AI tool.",
    payload: ["caseId", "subject", "priority", "contactEmail", "assignedAgent", "createdAt"]
  }
];

const ACTIONS = [
  {
    id: "get-contact",
    label: "Get Contact",
    icon: "👤",
    description: "Look up a Velaro contact record by email. Returns name, phone, company, and conversation history.",
    endpoint: "GET /Contacts/search?email={email}",
    requiresKey: true
  },
  {
    id: "create-lead",
    label: "Create Lead",
    icon: "📋",
    description: "Create a new lead in Velaro from data captured in your Power Automate flow.",
    endpoint: "POST /ContactManager/leads",
    requiresKey: true
  },
  {
    id: "create-case",
    label: "Create Case / Ticket",
    icon: "🎫",
    description: "Log a support case in Velaro's ticketing system from an external trigger.",
    endpoint: "POST /Tickets",
    requiresKey: true
  },
  {
    id: "send-message",
    label: "Send Message",
    icon: "💬",
    description: "Inject a bot message into an active conversation — useful for async updates from Power Automate.",
    endpoint: "POST /Conversations/{id}/messages",
    requiresKey: true
  },
  {
    id: "get-transcript",
    label: "Get Transcript",
    icon: "📄",
    description: "Retrieve the full conversation transcript. Useful for post-chat processing in your flow.",
    endpoint: "GET /Conversations/{id}/transcript",
    requiresKey: true
  }
];

const SAMPLE_PAYLOAD = `{
  "jobId": 12345,
  "siteId": 1001,
  "conversationId": 98765,
  "contactEmail": "visitor@example.com",
  "contactName": "John Smith",
  "agentName": "Sarah Lee",
  "sentiment": "positive",
  "channel": "Web",
  "workflowVariables": {
    "orderNumber": "ORD-1234",
    "issueType": "Billing"
  }
}`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function PowerAutomateLogoIcon() {
  return (
    <img src="/img/ms-power-automate.svg" alt="Power Automate" className="w-8 h-8" />
  );
}

function StatusBadge({ configured }: { configured: boolean }) {
  if (configured) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
      <span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />
      Not configured
    </span>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className={`text-xs px-2.5 py-1 rounded border transition-colors font-medium ${
        copied
          ? "border-green-300 bg-green-50 text-green-700"
          : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
      }`}
    >
      {copied ? "✓ Copied" : (label ?? "Copy")}
    </button>
  );
}

// ─── Locked state (subscription gate) ────────────────────────────────────────

function LockedState() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-10 flex flex-col items-center text-center gap-4">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
        <PowerAutomateLogoIcon />
      </div>
      <div>
        <div className="text-lg font-semibold text-slate-800 mb-1">Microsoft Power Automate</div>
        <div className="text-sm text-slate-500 max-w-md mx-auto">
          Trigger Power Automate flows from Velaro events and call Velaro actions from inside your flows.
          Upgrade your plan to unlock this integration.
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span className="text-sm text-slate-600">Power Automate is available on Pro and Enterprise plans.</span>
      </div>
      <Link
        to="/billing"
        className="mt-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white"
        style={{ background: "var(--color-primary)" }}
      >
        Upgrade Plan →
      </Link>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type Tab = "setup" | "triggers" | "actions" | "logs";

export default function PowerAutomatePage() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [config, setConfig] = useState<Config | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [flowUrl, setFlowUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("setup");
  const [expandedTrigger, setExpandedTrigger] = useState<string | null>("chat-started");

  useEffect(() => {
    api.messaging.get("PowerAutomate/config")
      .then(r => r.ok ? r.json() : null)
      .then((data: Config | null) => {
        setConfig(data ?? { configured: false, displayName: "", flowUrlMasked: "" });
        if (data?.displayName) setDisplayName(data.displayName);
      })
      .catch(() => setConfig({ configured: false, displayName: "", flowUrlMasked: "" }))
      .finally(() => setLoadingConfig(false));
  }, [api.messaging]);

  async function save() {
    if (!flowUrl.trim()) { setSaveError("Power Automate Flow URL is required"); return; }
    setSaving(true);
    setSaveError("");
    try {
      const res = await api.messaging.post("PowerAutomate/config", { flowUrl, displayName });
      if (res.ok) {
        setSaved(true);
        const updated: Config = await api.messaging.get("PowerAutomate/config").then(r => r.json());
        setConfig(updated);
        setFlowUrl("");
        setTimeout(() => setSaved(false), 3000);
      } else {
        const body = await res.text().catch(() => "");
        setSaveError(`Save failed (HTTP ${res.status})${body ? `: ${body}` : ""}. Check the URL and try again.`);
      }
    } catch (err) {
      setSaveError(`Network error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    if (!config?.configured) {
      setTestResult({ success: false, message: "No flow URL configured. Save a URL first." });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.messaging.post("PowerAutomate/test", {});
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setTestResult({ success: true, message: `Test delivered successfully. Flow responded with HTTP ${data.statusCode ?? "2xx"}.` });
      } else {
        const body = await res.text().catch(() => "");
        setTestResult({ success: false, message: `Test failed (HTTP ${res.status})${body ? `: ${body}` : ""}` });
      }
    } catch (err) {
      setTestResult({ success: false, message: `Connection error: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setTesting(false);
    }
  }

  // Subscription gate — show locked state but don't hide the card from the landing page
  if (subscription && !subscription.enablePowerAutomate) {
    return <LockedState />;
  }

  if (loadingConfig) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "setup", label: "Setup" },
    { key: "triggers", label: "Triggers" },
    { key: "actions", label: "Actions" },
    { key: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl border border-slate-200 bg-white flex items-center justify-center shadow-sm p-2">
            <PowerAutomateLogoIcon />
          </div>
          <div>
            <div className="text-lg font-semibold text-slate-800">Microsoft Power Automate</div>
            <div className="text-sm text-slate-500">
              Trigger flows from chat events — bidirectional data exchange, no code required
            </div>
          </div>
        </div>
        <StatusBadge configured={config?.configured ?? false} />
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-slate-200 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`py-2 mr-4 text-sm cursor-pointer ${
              activeTab === tab.key
                ? "border-b-2 border-primary text-primary font-medium"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── SETUP TAB ── */}
      {activeTab === "setup" && (
        <div className="space-y-4">
          {/* How it works */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="font-semibold text-slate-800 mb-4">How Power Automate works with Velaro</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  icon: "⚡",
                  title: "Event Triggers",
                  desc: "Velaro fires events (chat started, lead created, etc.) to your Power Automate HTTP trigger URL automatically."
                },
                {
                  icon: "🤖",
                  title: "AI Workflow Node",
                  desc: "Any AI bot or workflow node can call your flow mid-conversation and use the response as variables."
                },
                {
                  icon: "🔄",
                  title: "Velaro Actions",
                  desc: "Your flow can call Velaro REST endpoints — create leads, fetch transcripts, send messages — using your API key."
                }
              ].map(f => (
                <div key={f.title} className="rounded-xl border border-slate-200 p-4">
                  <div className="text-2xl mb-2">{f.icon}</div>
                  <div className="font-medium text-slate-800 text-sm mb-1">{f.title}</div>
                  <div className="text-xs text-slate-500">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Connection status */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="font-semibold text-slate-800 mb-4">Connection Status</div>

            {config?.configured && (
              <div className="mb-5 rounded-xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-green-800 mb-0.5">
                      {config.displayName || "Power Automate flow"} — connected
                    </div>
                    <div className="text-xs text-green-700 font-mono">{config.flowUrlMasked}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={testConnection}
                      disabled={testing}
                      className="text-xs px-3 py-1.5 rounded-lg bg-white border border-green-300 text-green-700 hover:bg-green-100 font-medium disabled:opacity-60"
                    >
                      {testing ? "Testing…" : "Test connection"}
                    </button>
                  </div>
                </div>
                {testResult && (
                  <div className={`mt-3 text-xs rounded-lg px-3 py-2 ${testResult.success ? "bg-green-100 text-green-800" : "bg-red-50 text-red-700"}`}>
                    {testResult.success ? "✓ " : "✗ "}{testResult.message}
                  </div>
                )}
                <div className="mt-3 text-xs text-green-600">
                  Rate limit: up to 60 trigger calls/minute on your plan.
                </div>
              </div>
            )}

            {/* Quick setup steps */}
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-5">
              <div className="font-semibold text-blue-800 mb-3 text-sm">Quick Setup (3 steps)</div>
              <ol className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">1</span>
                  <span>In Power Automate, create a new flow with the <strong>When an HTTP request is received</strong> trigger.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">2</span>
                  <span>Copy the <strong>HTTP POST URL</strong> from that trigger step and paste it below.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">3</span>
                  <span>Add a <strong>Power Automate</strong> node to any workflow — or enable automatic event triggers in the Triggers tab.</span>
                </li>
              </ol>
            </div>

            {/* Config form */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display Name (optional)</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="e.g. Lead Capture Flow"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Power Automate Flow URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="https://prod-00.eastus.logic.azure.com/workflows/..."
                  value={flowUrl}
                  onChange={e => { setFlowUrl(e.target.value); setSaveError(""); }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  The HTTP POST URL from your Power Automate <em>When an HTTP request is received</em> trigger.
                </p>
              </div>

              {saveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {saveError}
                </div>
              )}
              {saved && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
                  ✓ Saved — your flow URL is active.
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 shadow-sm"
                  style={{ background: "var(--color-primary)" }}
                >
                  {saving ? "Saving…" : (config?.configured ? "Update Flow URL" : "Connect Power Automate")}
                </button>
              </div>
            </div>
          </div>

          {/* Payload reference */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="font-semibold text-slate-800 mb-2">What Velaro sends to your flow</div>
            <p className="text-sm text-slate-500 mb-3">
              Every trigger call POSTs this JSON to your HTTP trigger URL. Use <code className="bg-slate-100 px-1 rounded text-xs">workflowVariables</code> to
              pass data set by earlier workflow nodes.
            </p>
            <div className="flex items-start gap-2 mb-2 justify-end">
              <CopyButton text={SAMPLE_PAYLOAD} label="Copy payload" />
            </div>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-4 overflow-auto text-slate-700 leading-relaxed">
              {SAMPLE_PAYLOAD}
            </pre>
            <p className="text-xs text-slate-500 mt-3">
              Your flow can return a JSON object with key-value pairs — Velaro saves them as workflow variables
              accessible in later nodes as <code className="bg-slate-100 px-1 rounded">{"<<workflow.keyName>>"}</code>.
            </p>
          </div>

          {/* API Key section */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="font-semibold text-slate-800 mb-1">API Key for Velaro Actions</div>
            <p className="text-sm text-slate-500 mb-4">
              To call Velaro endpoints from Power Automate (create leads, fetch transcripts, send messages),
              your flow needs a Velaro API key. Create one below and add it as an HTTP header in your flow.
            </p>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Create an API key</div>
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 font-mono">
                  velaro mcp-key create --label "Power Automate"
                </code>
                <CopyButton text='velaro mcp-key create --label "Power Automate"' />
              </div>
              <p className="text-xs text-slate-500">
                Run this in your terminal after installing the Velaro CLI (<code className="bg-slate-100 px-1 rounded">npm install -g @velaro/cli</code>).
                The key is shown once — store it securely as a Power Automate connection or secret.
              </p>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">Add to your HTTP action in Power Automate</div>
              <div className="space-y-1.5 text-xs text-blue-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium w-20 shrink-0">Header:</span>
                  <code className="bg-white border border-blue-100 rounded px-2 py-0.5">X-Velaro-Key</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium w-20 shrink-0">Value:</span>
                  <code className="bg-white border border-blue-100 rounded px-2 py-0.5">vel_live_your_key_here</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium w-20 shrink-0">Base URL:</span>
                  <code className="bg-white border border-blue-100 rounded px-2 py-0.5">https://api-admin-us-east.velaro.com</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TRIGGERS TAB ── */}
      {activeTab === "triggers" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="font-semibold text-slate-800 mb-1">Event Triggers</div>
            <p className="text-sm text-slate-500 mb-5">
              These are Velaro events that POST data to your Power Automate flow URL.
              Triggers fire automatically when the event occurs — no workflow node needed.
              The workflow <strong>Power Automate</strong> node fires on-demand from inside a workflow.
            </p>

            <div className="space-y-3">
              {TRIGGERS.map(trigger => (
                <div key={trigger.id} className="rounded-xl border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50"
                    onClick={() => setExpandedTrigger(expandedTrigger === trigger.id ? null : trigger.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{trigger.icon}</span>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{trigger.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{trigger.description}</div>
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${expandedTrigger === trigger.id ? "rotate-180" : ""}`}
                      viewBox="0 0 16 16" fill="currentColor"
                    >
                      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                    </svg>
                  </button>

                  {expandedTrigger === trigger.id && (
                    <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Fields included in payload
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {trigger.payload.map(field => (
                          <code
                            key={field}
                            className="text-xs bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-700 font-mono"
                          >
                            {field}
                          </code>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-3">
                        Plus: <code className="bg-white border border-slate-200 rounded px-1">siteId</code>,{" "}
                        <code className="bg-white border border-slate-200 rounded px-1">workflowVariables</code> (all variables set in the workflow),
                        and <code className="bg-white border border-slate-200 rounded px-1">jobId</code> for deduplication.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── ACTIONS TAB ── */}
      {activeTab === "actions" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="font-semibold text-slate-800 mb-1">Velaro Actions</div>
            <p className="text-sm text-slate-500 mb-5">
              Call these Velaro REST endpoints from inside your Power Automate flow.
              All actions require a <code className="bg-slate-100 px-1 rounded text-xs">vel_live_*</code> API key
              in the <code className="bg-slate-100 px-1 rounded text-xs">X-Velaro-Key</code> header.
              See the Setup tab for instructions.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ACTIONS.map(action => (
                <div
                  key={action.id}
                  className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex gap-3"
                >
                  <div className="text-xl shrink-0">{action.icon}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800 mb-0.5">{action.label}</div>
                    <div className="text-xs text-slate-600 mb-2">{action.description}</div>
                    <code className="text-[10px] bg-white border border-blue-100 rounded px-2 py-0.5 text-slate-600 font-mono block">
                      {action.endpoint}
                    </code>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Base URL</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 font-mono">
                  https://api-admin-us-east.velaro.com
                </code>
                <CopyButton text="https://api-admin-us-east.velaro.com" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ACTIVITY LOG TAB ── */}
      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="PowerAutomate" />
        </div>
      )}
    </div>
  );
}
